import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { css } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'

import { Avatar } from '../shared/avatar'
import { Timestamp } from '../timekeeping/timestamp'
import { useNavToTopic } from '../../hooks/app_location/use_nav_to_topic'
import { useHandleMetadata } from '../../hooks/use_handle_metadata'
import { makeGetChat } from '../../reduxes/slices/api/selectors'
import { getCurrentHandle } from '../../reduxes/slices/current/selectors'
import { makeGetLatestChatTimeline } from '../../reduxes/slices/timelines/selectors'
import { WIDGET_VIEW } from '../../pages/admin/app_location_provider'

type Props = {
  topic: any
} & React.HTMLAttributes<HTMLDivElement>

export const LiveChatPreview = ({ topic, ...props }: Props) => {
  const navToTopic = useNavToTopic()
  const getLatestChatTimeline = useMemo(makeGetLatestChatTimeline, [])
  const latestChatTimeline = useSelector((state) =>
    getLatestChatTimeline(state, topic.id),
  )
  const getChat = useMemo(makeGetChat, [])
  const chat = useSelector((state) => getChat(state, latestChatTimeline?.tl_id))
  const currentHandle = useSelector(getCurrentHandle)
  const { name, contact } = useHandleMetadata(chat?.handle)

  const onClick = () => {
    navToTopic(topic.id, { widgetView: WIDGET_VIEW.MESSENGER })
  }

  return (
    <Stack
      as={Button}
      direction={'row'}
      onClick={onClick}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      css={{
        padding: '$4',
        borderRadius: '$4 !important',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        width: '100%',
        color: '$grey950',
      }}
      {...props}
    >
      <Avatar
        handle={contact?.display_email || chat?.handle}
        className={avatarStyles()}
      />
      <Stack
        gap={1}
        css={{
          alignItems: 'flex-start',
          marginRight: '30px',
          '>div': { textAlign: 'left', wordBreak: 'break-all' },
        }}
      >
        <Stack direction={'row'} gap={2} css={{ '>b': { fontSize: '$3' } }}>
          <b>{!chat || chat.handle === currentHandle ? 'You' : name}</b>
          {chat && (
            <Timestamp date={chat?.created_at} className={timestampStyles()} />
          )}
        </Stack>
        <div>{chat ? truncateChatText(chat.plain) : 'Call started'}</div>
      </Stack>
      <Stack
        css={{
          alignSelf: 'stretch',
          justifyContent: 'center',
          alignItems: 'flex-end',
          flexGrow: 1,
          fontSize: '$4',
          color: '$grey700',

          i: {
            color: '$grey400',
          },
        }}
      >
        <i className="fas fa-chevron-right" />
      </Stack>
    </Stack>
  )
}

const MAX_LENGTH = 256
const truncateChatText = (text: string) => {
  if (text && text.length > MAX_LENGTH) {
    return text.slice(0, MAX_LENGTH) + '...'
  } else {
    return text
  }
}

const avatarStyles = css({
  marginRight: '$4',
})

const timestampStyles = css({
  fontSize: '$0',
  color: '$grey500',
  marginLeft: '$2',
})
